import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Container, Grid, Typography,GlobalStyles } from "@mui/material";
import OurStory from "./images/our-story.jpg";
import { Sparkle, ArrowUpRight } from "lucide-react";
import FounderPhoto from "./images/himanshu-singla.webp";

/* ── Values / pillars ─────────────────────────────────── */
const values = [
  {
    icon: "🔍",
    title: "Radical Transparency",
    body: "Every metric you see on Meesha is real. No inflated follower counts, no fake reach estimates. We surface raw audience data so you can make decisions grounded in truth, not guesswork.",
  },
  {
    icon: "⚡",
    title: "Speed Over Friction",
    body: "Finding the right creator should take minutes, not weeks. We've compressed the research, outreach, and vetting process into a single workflow so teams move faster and waste less.",
  },
  {
    icon: "🎯",
    title: "Intelligence That Compounds",
    body: "Every campaign run through Meesha feeds our models. Audience quality benchmarks sharpen. Contact success rates improve. The platform gets smarter the more it's used.",
  },
];

/* ── Stats ────────────────────────────────────────────── */
const stats = [
  { v: "2M+",  l: "Creators indexed" },
  { v: "95%",  l: "Contact reach rate" },
  { v: "10×",  l: "Faster than manual" },
  { v: "500+", l: "Campaigns powered" },
];
const palette = {
  ink: "#FFFFFF",
  // paper: "#OD1B3E",
  paper: "#0D1B3E",
  // volt: "#D4FF3D",
  volt:"#3B82F6",
  coral: "#FF5A36",
  slate: "#6B6875",
  line: "#E4E0D6",
  founderText:'#FF6835',
};

function AboutUsBody() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
   
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&display=swap";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);
    useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <Box sx={{ bgcolor: "#060F24", minHeight: "100vh" }}>

      {/* ── Hero header ── */}
      <Box sx={{
        bgcolor:    "#0D1B3E",
        pt:         { xs: "120px", md: "160px" },
        pb:         { xs: "64px",  md: "90px"  },
        px:         { xs: "20px",  md: "80px"  },
        position:   "relative",
        overflow:   "hidden",
        textAlign:  "center",
      }}>
        {/* ambient glow */}
        <Box sx={{
          position:   "absolute",
          width:      500,
          height:     500,
          top:        "-100px",
          left:       "50%",
          transform:  "translateX(-50%)",
          background: "radial-gradient(circle, rgba(255,107,53,0.08), transparent 65%)",
          filter:     "blur(60px)",
          pointerEvents: "none",
        }} />

        <Box component="span" sx={{
          display:       "inline-block",
          color:         "#FF6B35",
          bgcolor:       "rgba(255,107,53,0.1)",
          border:        "1px solid rgba(255,107,53,0.3)",
          px:            "16px",
          py:            "6px",
          borderRadius:  "100px",
          fontSize:      "11px",
          fontWeight:    700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          mb:            3,
          display:       "inline-block",
        }}>
          Our Story
        </Box>

        <Typography component="h1" sx={{
          fontSize:   "clamp(32px, 4.5vw, 56px)",
          fontWeight: 800,
          color:      "white",
          lineHeight: 1.15,
          maxWidth:   "720px",
          mx:         "auto",
          mb:         2.5,
        }}>
          We're on a mission to make every{" "}
          <Box component="span" sx={{
            background:           "linear-gradient(90deg, #FF6B35 0%, #FF9F6B 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor:  "transparent",
            backgroundClip:       "text",
          }}>
            creator partnership count.
          </Box>
        </Typography>

        <Typography sx={{
          fontSize:   "17px",
          color:      "rgba(255,255,255,0.55)",
          maxWidth:   "580px",
          mx:         "auto",
          lineHeight: 1.75,
        }}>
          Meesha was built by a team that ran influencer campaigns the hard way — spreadsheets, cold DMs, agencies that over-charged and under-delivered. We decided to fix it.
        </Typography>
      </Box>

      {/* ── Stats strip ── */}
      <Box sx={{
        bgcolor:             "#0a1630",
        borderTop:           "1px solid rgba(255,255,255,0.05)",
        borderBottom:        "1px solid rgba(255,255,255,0.05)",
        display:             "grid",
        gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
        py:                  "36px",
        px:                  { xs: "20px", md: "80px" },
      }}>
        {stats.map((s, i) => (
          <Box key={i} sx={{
            textAlign:   "center",
            borderRight: { md: i < 3 ? "1px solid rgba(255,255,255,0.06)" : "none" },
            py:          { xs: "12px", md: 0 },
          }}>
            <Typography sx={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 800, color: "#FF6B35", lineHeight: 1 }}>
              {s.v}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.4)", mt: 0.8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {s.l}
            </Typography>
          </Box>
        ))}
      </Box>
     {/*---About Founder section --*/}

<GlobalStyles
  styles={{
    "@keyframes founderFadeUp": {
      from: { opacity: 0, transform: "translateY(32px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    "@keyframes founderFloat": {
      "0%, 100%": { transform: "translateY(0) rotate(-4deg)" },
      "50%": { transform: "translateY(-14px) rotate(-1.5deg)" },
    },
    "@keyframes founderBlob": {
      "0%, 100%": { transform: "scale(1)", opacity: 0.55 },
      "50%": { transform: "scale(1.1)", opacity: 0.8 },
    },
    "@keyframes founderSpin": {
      "0%":   { transform: "rotate(-36deg)" },
      "100%": { transform: "rotate(324deg)" },
    },
    "@media (prefers-reduced-motion: reduce)": {
      "*": {
        animationDuration: "0.001ms !important",
        animationIterationCount: "1 !important",
        transitionDuration: "0.001ms !important",
      },
    },
  }}
/>

<Box ref={sectionRef} sx={{ bgcolor: palette.paper }}>
  <Container
    maxWidth="xl"
    sx={{ 
      py: { xs: "64px", md: "100px" }, 
      px: { xs: "20px", sm: "40px", md: "80px" } 
    }}
  >
   <Grid 
  container 
  spacing={{ xs: 6, md: 8, lg: 10 }} 
  alignItems="center" 
  justifyContent="center"
  direction={{ xs: "column-reverse", md: "row" }}
>
  {/* Text */}
  <Grid
    item
    xs={12}
    md={5}
    sx={{
      opacity: visible ? 1 : 0,
      animation: visible ? "founderFadeUp 0.8s cubic-bezier(.2,.7,.2,1) both" : "none",
      textAlign: { xs: "center", md: "left" },
      display: "flex",
      flexDirection: "column",
      alignItems: { xs: "center", md: "flex-start" },
      width: "100%",
    }}
  >
    <Box sx={{ 
      display: "flex", 
      alignItems: "center", 
      gap: 1, 
      mb: 3,
      justifyContent: { xs: "center", md: "flex-start" }
    }}>
      <Sparkle size={16} color={palette.ink} strokeWidth={2.5} />
      <Typography
        component="span"
        sx={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "13px",
          fontWeight: 600,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: palette.slate,
        }}
      >
        Founder&rsquo;s Note
      </Typography>
    </Box>

    <Typography
      component="h2"
      sx={{
        fontFamily: "'Space Grotesk', sans-serif",
        fontWeight: 700,
        color: palette.founderText,
        fontSize: { xs: "28px", sm: "34px", md: "42px", lg: "48px" },
        lineHeight: 1.05,
        letterSpacing: "-0.02em",
        mb: 3,
        textAlign: { xs: "center", md: "left" },
        width: "100%",
      }}
    >
      Meet Our Founder
    </Typography>

    <Typography
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontSize: { xs: "15px", sm: "15.5px", md: "16px", lg: "17px" },
        lineHeight: 1.75,
        color: "#FFFFFF8C",
        mb: 2.5,
        textAlign: { xs: "center", md: "left" },
        width: "100%",
      }}
    >
     Himanshu Singla is the Founder & CEO of Meesha, driven by a passion for building technology that solves real marketing challenges. With years of experience in influencer marketing and digital strategy, he has worked closely with brands, agencies, and creators to understand the evolving creator economy. This experience inspired him to build Meesha a platform that makes influencer marketing more efficient, transparent, and data-driven. 
    </Typography>

    <Typography
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontSize: { xs: "15px", sm: "15.5px", md: "16px", lg: "17px" },
        lineHeight: 1.75,
        color: "#FFFFFF8C",
        textAlign: { xs: "center", md: "left" },
        width: "100%",
        mb: 3.5,
      }}
    >
   Himanshu believes technology should simplify complex processes and help businesses make smarter decisions. His vision is to create solutions that empower brands to build meaningful creator partnerships while delivering measurable results. Through innovation and a user-first approach, he continues to shape Meesha into a platform that drives the future of influencer marketing..
    </Typography>

    {/* Pull quote */}
    {/* <Typography
      sx={{
        fontFamily: "'Space Grotesk', sans-serif",
        fontStyle: "italic",
        fontWeight: 500,
        fontSize: { xs: "16px", md: "18px" },
        lineHeight: 1.5,
        color: palette.coral,
        borderLeft: { xs: "none", md: `2px solid ${palette.coral}` },
        pl: { xs: 0, md: 2.5 },
        mb: 3.5,
        textAlign: { xs: "center", md: "left" },
      }}
    >
      "Real data, not follower counts — that's what makes a partnership work."
    </Typography> */}

    {/* Stat chips */}
    <Box sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: 1.5,
      justifyContent: { xs: "center", md: "flex-start" },
    }}>
      {[
        { v: "10+", l: "Years Experience" },
        { v: "NSIT", l: "Engineering Grad" },
      ].map((chip, i) => (
        <Box
          key={i}
          sx={{
            border: "1px solid rgba(255,255,255,0.12)",
            bgcolor: "rgba(255,255,255,0.03)",
            borderRadius: "10px",
            px: "14px",
            py: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "white", lineHeight: 1.2 }}>
            {chip.v}
          </Typography>
          <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {chip.l}
          </Typography>
        </Box>
      ))}
    </Box>
  </Grid>

  {/* Image */}
  <Grid
    item
    xs={12}
    md={7}
    sx={{
      opacity: visible ? 1 : 0,
      animation: visible
        ? "founderFadeUp 0.9s cubic-bezier(.2,.7,.2,1) 0.15s both"
        : "none",
      display: "flex",
      justifyContent: "center",
      width: "100%",
    }}
  >
    <Box
      sx={{
        position: "relative",
        width: { xs: "100%", sm: "80%", md: "100%" },
        // maxWidth: { xs: "320px", sm: "380px", md: "420px", lg: "460px" },
        maxWidth: { xs: "260px", sm: "300px", md: "340px", lg: "370px" },
        mx: "auto",
        transition: "transform 0.4s cubic-bezier(.2,.8,.2,1)",
        "&:hover": {
          transform: { md: "rotate(1.5deg) scale(1.015)" },
        },
      }}
    >
      {/* Ambient blob */}
      {/* <Box
        sx={{
          position: "absolute",
          top: { xs: "-5%", md: "-8%" },
          right: { xs: "-5%", md: "-6%" },
          width: { xs: "180px", sm: "220px", md: "280px", lg: "320px" },
          height: { xs: "180px", sm: "220px", md: "280px", lg: "320px" },
          borderRadius: "50%",
          background: palette.coral,
          filter: "blur(60px)",
          animation: "founderBlob 6s ease-in-out infinite",
          zIndex: 0,
          opacity: 0.6,
        }}
      /> */}

      {/* Rotating lime card behind photo */}
      {/* <Box
        sx={{
          position: "absolute",
          inset: 5,
          animation: "founderSpin 22s linear infinite",
          bgcolor: palette.volt,
          borderRadius: "18px",
          zIndex: 1,
          opacity: 0.85,
          boxShadow: "0 20px 50px -10px rgba(212,255,61,0.25)",
        }}
      /> */}
      {/* Photo */}
        <Box
          component="img"
          src={FounderPhoto}
          alt="Himanshu Singla, Founder & CEO of Idiotic Media"
          sx={{
            position: "relative",
            zIndex: 2,
            display: "block",
            width: 435,
            height: 435,
            objectFit: "contain",
            // borderRadius: "18px",
            borderRadius: "50%",
            border: `2px solid ${palette.coral}`,
            // boxShadow: "0 30px 60px -20px rgba(20,19,26,0.35)",
            borderWidth:"8px",
            borderColor:'#FF6B35'
          }}
        />
    </Box>
  </Grid>
</Grid>

    
  </Container>
</Box>    
      {/* ── Our story — two-column ── */}
      <Container maxWidth="xl" sx={{ py: { xs: "64px", md: "100px" }, px: { xs: "20px", md: "80px" } }}>
        <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">

          {/* Image */}
          <Grid item xs={12} md={5}>
            <Box sx={{
              borderRadius: "24px",
              overflow:     "hidden",
              position:     "relative",
              boxShadow:    "0 40px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)",
            }}>
              <Box
                component="img"
                src={OurStory}
                alt="Our Story"
                sx={{ width: "100%", display: "block", objectFit: "cover", filter: "brightness(0.85)" }}
              />
              {/* Coral tint overlay */}
              <Box sx={{
                position:   "absolute",
                inset:      0,
                background: "linear-gradient(135deg, rgba(255,107,53,0.12) 0%, transparent 60%)",
                pointerEvents: "none",
              }} />
            </Box>
          </Grid>

          {/* Story text */}
          <Grid item xs={12} md={7}>
            <Box component="span" sx={{
              display:       "inline-block",
              color:         "#FF6B35",
              bgcolor:       "rgba(255,107,53,0.1)",
              border:        "1px solid rgba(255,107,53,0.25)",
              px:            "30px",
              py:            "15px",
              borderRadius:  "100px",
              fontSize:      "15px",
              fontWeight:    700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb:            3,
           
         
            }}>
              How it started
            </Box>

            <Typography sx={{
              fontSize:   "clamp(22px, 2.8vw, 32px)",
              fontWeight: 800,
              color:      "white",
              lineHeight: 1.3,
              mb:         3,
            }}>
              The problem wasn't finding creators. It was knowing which ones actually worked.
            </Typography>

            {[
              "Every brand we spoke to had the same story: they'd partnered with a creator with 500K followers, paid lakhs for a campaign, and got three sales. The audience looked right. The content was fine. But the data behind the decision was hollow.",
              "Meesha started as a simple idea — what if you could see the real health of an audience before you commit? Not just follower counts, but bot ratios, engagement authenticity, demographic accuracy, and direct verified contact — all in one place.",
              "Today, Meesha indexes 2M+ Indian creators across Instagram. Every profile carries a deep intelligence layer: audience quality scores, verified phone and email contacts, campaign performance benchmarks, and a full analytics suite. We're not a database. We're the intelligence engine behind India's most effective creator campaigns.",
            ].map((para, i) => (
              <Typography key={i} sx={{
                fontSize:   "15px",
                color:      "rgba(255,255,255,0.58)",
                lineHeight: 1.8,
                mb:         2,
              }}>
                {para}
              </Typography>
            ))}

            <Button
              href="/authenticate/signup"
              sx={{
                mt:            1,
                background:    "linear-gradient(135deg, #FF6B35 0%, #FF4500 100%)",
                color:         "white",
                fontWeight:    700,
                fontSize:      "14px",
                px:            "28px",
                py:            "13px",
                borderRadius:  "12px",
                textTransform: "none",
                boxShadow:     "0 4px 20px rgba(255,107,53,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #e85520 0%, #cc3300 100%)",
                  transform:  "translateY(-2px)",
                  boxShadow:  "0 10px 36px rgba(255,107,53,0.45)",
                },
                transition:    "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            >
              Start discovering creators free →
            </Button>
          </Grid>
        </Grid>
      </Container>

      {/* ── Values ── */}
      <Box sx={{
        bgcolor:  "#0a1630",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        py:       { xs: "64px", md: "96px" },
        px:       { xs: "20px", md: "80px" },
      }}>
        <Box sx={{ textAlign: "center", mb: { xs: "40px", md: "64px" } }}>
          <Box component="span" sx={{
            display:       "inline-block",
            color:         "#FF6B35",
            bgcolor:       "rgba(255,107,53,0.1)",
            border:        "1px solid rgba(255,107,53,0.25)",
            px:            "14px",
            py:            "5px",
            borderRadius:  "100px",
            fontSize:      "10px",
            fontWeight:    700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            mb:            2.5,
          }}>
            What we believe
          </Box>
          <Typography sx={{
            fontSize:   "clamp(24px, 3vw, 36px)",
            fontWeight: 800,
            color:      "white",
            lineHeight: 1.2,
          }}>
            Principles that shape every decision we make
          </Typography>
        </Box>

        <Container maxWidth="lg" disableGutters>
          <Grid container spacing={3}>
            {values.map((v, i) => (
              <Grid key={i} item xs={12} md={4}>
                <Box sx={{
                  height:       "100%",
                  bgcolor:      "rgba(255,255,255,0.03)",
                  border:       "1px solid rgba(255,255,255,0.07)",
                  borderRadius: "20px",
                  p:            "32px 28px",
                  transition:   "all 0.3s ease",
                  "&:hover": {
                    bgcolor:     "rgba(255,255,255,0.055)",
                    borderColor: "rgba(255,107,53,0.3)",
                    transform:   "translateY(-6px)",
                    boxShadow:   "0 20px 60px rgba(0,0,0,0.25)",
                  },
                }}>
                  <Typography sx={{ fontSize: "32px", mb: 2 }}>{v.icon}</Typography>
                  <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "white", mb: 1.5, lineHeight: 1.3 }}>
                    {v.title}
                  </Typography>
                  <Typography sx={{ fontSize: "14px", color: "rgba(255,255,255,0.5)", lineHeight: 1.78 }}>
                    {v.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ bgcolor: "#0D1B3E", textAlign: "center", py: { xs: "64px", md: "96px" }, px: "20px", position: "relative", overflow: "hidden" }}>
        <Box sx={{
          position:   "absolute",
          width:      400,
          height:     400,
          bottom:     "-100px",
          right:      "10%",
          background: "radial-gradient(circle, rgba(255,107,53,0.08), transparent 65%)",
          filter:     "blur(60px)",
          pointerEvents: "none",
        }} />
        <Typography sx={{
          fontSize:   "clamp(24px, 3vw, 36px)",
          fontWeight: 800,
          color:      "white",
          mb:         2,
          position:   "relative",
          zIndex:     1,
        }}>
          Ready to run smarter campaigns?
        </Typography>
        <Typography sx={{
          fontSize:  "16px",
          color:     "rgba(255,255,255,0.5)",
          mb:        4,
          position:  "relative",
          zIndex:    1,
        }}>
          Join 500+ brands already using Meesha to find and activate the right creators.
        </Typography>
        <Button
          href="/authenticate/signup"
          sx={{
            position:      "relative",
            zIndex:        1,
            background:    "linear-gradient(135deg, #FF6B35 0%, #FF4500 100%)",
            color:         "white",
            fontWeight:    700,
            fontSize:      "15px",
            px:            "32px",
            py:            "14px",
            borderRadius:  "12px",
            textTransform: "none",
            boxShadow:     "0 4px 24px rgba(255,107,53,0.4)",
            "&:hover": {
              background: "linear-gradient(135deg, #e85520 0%, #cc3300 100%)",
              transform:  "translateY(-2px)",
              boxShadow:  "0 12px 40px rgba(255,107,53,0.5)",
            },
            transition:    "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        >
          Get started free →
        </Button>
      </Box>
    </Box>
  );
}

export default AboutUsBody;
